import { ChangeDetectionStrategy, Component, Inject, Injector, Input, OnDestroy, OnInit } from '@angular/core';
import {
	ControlValueAccessor,
	FormControl,
	FormControlDirective,
	FormControlName,
	FormGroupDirective,
	NG_VALUE_ACCESSOR,
	NgControl,
	NgModel,
	ValidationErrors,
	ValidatorFn,
	Validators,
} from '@angular/forms';
import { LanguageService } from '@core/services/language.service';
import { Language } from '@shared/model/languages.enum';
import { Country } from '@shared/sf-form-controls/sf-country-selector/sf-country-selector/country-selector.model';
import {
	countryList,
	findSelectedCountryByIso,
} from '@shared/sf-form-controls/sf-country-selector/sf-country-selector/country-selector.utils';
import { BehaviorSubject, combineLatest, Observable, skip, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

@Component({
	selector: 'sf-country-selector',
	templateUrl: './sf-country-selector.component.html',
	styleUrls: ['./sf-country-selector.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			multi: true,
			useExisting: SfCountrySelectorComponent,
		},
	],
})
export class SfCountrySelectorComponent implements OnInit, OnDestroy, ControlValueAccessor {
	@Input() public placeholder: string;
	@Input() public label: string;
	@Input() public requiredAsterisk: boolean = false;

	public currentLanguage = this.languageService.currentLanguage;
	public input: BehaviorSubject<any> = new BehaviorSubject<any>(null);
	public control: FormControl;
	public inputControl: FormControl<string> = new FormControl<string>('');
	public filteredCountryList: Observable<Country[]>;
	public disabled: boolean;

	onTouched: () => void;

	private readonly destroy: Subject<void> = new Subject();
	private selectedValue: BehaviorSubject<Country> = new BehaviorSubject<Country>(null);

	public get required(): boolean {
		return this.control ? this.control.hasValidator(Validators.required) : false;
	}

	constructor(
		@Inject(Injector) private readonly injector: Injector,
		private readonly languageService: LanguageService
	) {}

	public ngOnInit() {
		this.setControlInstance();

		this.inputControl.setValidators(this.countryFormValidator());
		if (this.control.hasValidator(Validators.required)) {
			this.inputControl.addValidators(Validators.required);
		}

		this.filteredCountryList = combineLatest([
			this.input,
			this.languageService.currentLanguage$.pipe(map((lang) => (lang === Language.PL ? Language.PL : Language.EN))),
			this.selectedValue,
		]).pipe(
			map(([_searchInput, language, selectedValue]: [string, Language, Country]) => {
				return selectedValue
					? [selectedValue]
					: countryList
							.filter((country) => country[language].toLowerCase().includes(this.inputControl.value.toLowerCase()))
							.sort((a, b) => a[language].localeCompare(b[language]));
			})
		);

		this.languageService.currentLanguage$
			.pipe(
				skip(1),
				tap((value) => {
					this.currentLanguage = value;
					if (this.selectedValue.value) {
						this.inputControl.setValue(this.inputControl.value);
					}
				}),
				takeUntil(this.destroy)
			)
			.subscribe();
	}

	public ngOnDestroy(): void {
		this.destroy.next();
		this.destroy.complete();
	}

	public onInput(event: Event): void {
		this.selectedValue.next(null);
		this.input.next(event);
		this.onChange(null);
		this.inputControl.updateValueAndValidity();
	}

	public writeValue(value: any) {
		if (!this.inputControl.value && value) {
			this.inputControl.setValue(value);
		}
		this.selectedValue.next(value ? findSelectedCountryByIso(value) : null);
	}

	public registerOnChange(fn: (countryData: any) => void) {
		this.onChange = fn;
	}

	public registerOnTouched(fn: () => void) {
		this.onTouched = fn;
	}

	public setDisabledState(isDisabled: boolean) {
		this.disabled = isDisabled;
		isDisabled ? this.inputControl.disable() : this.inputControl.enable();
	}

	public optionSelected(e) {
		this.writeValue(e.option.value);
		this.onChange(e.option.value);
		this.inputControl.updateValueAndValidity();
	}

	public displayFn(iso: string): string {
		const selectedCountry = countryList.find((country) => country.iso === iso);
		return selectedCountry ? (this.currentLanguage === Language.PL ? selectedCountry.pl : selectedCountry.en) : '';
	}

	private onChange: (value: number | string) => void;
	private countryFormValidator(): ValidatorFn {
		return (_): ValidationErrors | null => {
			return this.selectedValue.value || !this.inputControl.value ? null : { wrongCountryName: 'Wrong country name' };
		};
	}

	private setControlInstance(): void {
		const injectedControl = this.injector.get(NgControl);
		switch (injectedControl.constructor) {
			case NgModel: {
				this.control = (injectedControl as NgModel).control;
				break;
			}
			case FormControlName: {
				this.control = this.injector.get(FormGroupDirective).getControl(injectedControl as FormControlName);
				break;
			}
			default: {
				this.control = (injectedControl as FormControlDirective).form as FormControl;
				break;
			}
		}
	}
}
